import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import { MAIN_SITE_URL } from '@/mocks/site';

type TopicType = 'review' | 'shipping' | 'safety';

const topicConfigs: Record<string, { type: TopicType; titleKey: string; prefix: string }> = {
  review: { type: 'review', titleKey: 'topic.review.h1', prefix: 'topic.review' },
  'shipping-cost': { type: 'shipping', titleKey: 'topic.shipping.h1', prefix: 'topic.shipping' },
  safety: { type: 'safety', titleKey: 'topic.safety.h1', prefix: 'topic.safety' },
};

export default function Topic() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const config = slug ? topicConfigs[slug] : undefined;

  if (!config) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Page not found</h1>
          <Link to="/" className="mt-4 inline-block text-sm font-medium text-primary-600">
            {t('common.backHome')}
          </Link>
        </div>
      </Layout>
    );
  }

  const p = config.prefix;

  const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://usfanssheet.beer').replace(/\/$/, '');

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t(config.titleKey),
    description: t(`${p}.subtitle`),
    author: { '@type': 'Organization', name: 'USFans Spreadsheet' },
    publisher: { '@type': 'Organization', name: 'USFans Spreadsheet' },
    datePublished: '2026-08-20',
    dateModified: '2026-08-20',
    mainEntityOfPage: `${siteUrl}/topics/${slug}`,
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />

      <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-foreground-900">
            {t('nav.home')}
          </Link>
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm"></i>
          </span>
          <span className="text-foreground-900">{t(config.titleKey)}</span>
        </nav>

        <header className="mt-8">
          <h1 className="font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {t(config.titleKey)}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">{t(`${p}.subtitle`)}</p>
        </header>

        <div className="mt-8 space-y-8 text-[15px] leading-relaxed text-foreground-700">
          <p>{t(`${p}.intro`)}</p>

          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">{t(`${p}.section1Title`)}</h2>
            <p className="mt-3">{t(`${p}.section1Body`)}</p>
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">{t(`${p}.section2Title`)}</h2>
            <p className="mt-3">{t(`${p}.section2Body`)}</p>
          </div>

          {config.type === 'review' && (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                  <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                    <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                      <i className="ri-checkbox-circle-line text-lg"></i>
                    </span>
                    {t(`${p}.prosTitle`)}
                  </h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {[1, 2, 3].map((n) => (
                      <li key={n} className="flex items-start gap-2">
                        <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-600">
                          <i className="ri-check-line text-base"></i>
                        </span>
                        {t(`${p}.pro${n}`)}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                  <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                    <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                      <i className="ri-alert-line text-lg"></i>
                    </span>
                    {t(`${p}.consTitle`)}
                  </h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {[1, 2, 3].map((n) => (
                      <li key={n} className="flex items-start gap-2">
                        <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-accent-600">
                          <i className="ri-close-line text-base"></i>
                        </span>
                        {t(`${p}.con${n}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950">{t(`${p}.faqTitle`)}</h2>
                <div className="mt-3 flex flex-col gap-4">
                  {[1, 2].map((n) => (
                    <div key={n} className="rounded-lg border border-background-200 bg-background-50 p-5">
                      <p className="text-sm font-semibold text-foreground-950">{t(`${p}.q${n}`)}</p>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t(`${p}.a${n}`)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {config.type === 'shipping' && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-background-300">
                      <th className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-900">{t(`${p}.method`)}</th>
                      <th className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-900">{t(`${p}.speed`)}</th>
                      <th className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-900">{t(`${p}.cost`)}</th>
                      <th className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-900">{t(`${p}.tracking`)}</th>
                      <th className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-900">{t(`${p}.best`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3].map((n) => (
                      <tr key={n} className="border-b border-background-200 last:border-0">
                        <td className="whitespace-nowrap px-4 py-3 font-medium text-primary-700">{t(`${p}.r${n}.method`)}</td>
                        <td className="px-4 py-3 text-foreground-700">{t(`${p}.r${n}.speed`)}</td>
                        <td className="px-4 py-3 text-foreground-700">{t(`${p}.r${n}.cost`)}</td>
                        <td className="px-4 py-3 text-foreground-700">{t(`${p}.r${n}.tracking`)}</td>
                        <td className="px-4 py-3 text-foreground-600">{t(`${p}.r${n}.best`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <p className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center text-accent-600">
                    <i className="ri-lightbulb-line text-lg"></i>
                  </span>
                  {t(`${p}.tip`)}
                </p>
              </div>
            </>
          )}

          {config.type === 'safety' && (
            <>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950">{t(`${p}.section3Title`)}</h2>
                <p className="mt-3">{t(`${p}.section3Body`)}</p>
              </div>

              <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6">
                <h3 className="text-base font-semibold text-foreground-950">{t(`${p}.tipTitle`)}</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <li key={n} className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-secondary-600">
                        <i className="ri-checkbox-circle-line text-base"></i>
                      </span>
                      {t(`${p}.tip${n}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-arrow-right-up-line text-lg"></i>
            </span>
            {t('common.explore')}
          </a>
          <Link
            to="/"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-foreground-300 px-7 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-arrow-left-line text-lg"></i>
            </span>
            {t('common.backHome')}
          </Link>
        </div>
      </article>
    </Layout>
  );
}